import {
  ASSET_COMPONENT_KINDS,
  type AssetComponentDto,
  type AssetComponentKind,
  createComponentInput,
  createSupplierRenewalInput,
  type SupplierRenewalDto,
  updateComponentInput,
  updateSupplierRenewalInput,
} from '@fos/shared/contracts/maintenance';
import { type BillingCycle, nextCycleDate } from '@fos/shared/contracts/services';
import { ASSET_COMPONENT_KIND_LABELS, BILLING_CYCLE_LABELS } from '@fos/shared/labels';
import { Archive, RefreshCw } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { amountInputValue, issuesToErrors, parseAmountInput, zodIssues } from '../form-utils';
import { dateText } from '../maintenance-format';

const orNull = (v: string) => (v.trim() ? v.trim() : null);

function useDialogErrors<K extends string>(keys: readonly K[], open: boolean) {
  const [errors, setErrors] = useState<Partial<Record<K, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setErrors({});
      setFormError(null);
    }
  }, [open]);
  const fail = (err: unknown) => {
    if (appErrorCode(err) === 'VALIDATION_ERROR') {
      const fieldErrors = issuesToErrors(validationIssues(err), keys);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }
    }
    setFormError(
      appErrorCode(err) === 'CONFLICT' ? 'Ktoś zmienił te dane. Zamknij okno i spróbuj ponownie.' : errorMessage(err),
    );
  };
  return { errors, setErrors, formError, setFormError, fail };
}

// ── Element zasobu ──────────────────────────────────────────────────────────
const COMPONENT_KEYS = ['kind', 'description', 'provider', 'expiresOn', 'included'] as const;

/** Dodanie / edycja elementu zasobu (hosting, domena, SSL, baza, skrzynka): opis, dostawca, ważność, w pakiecie. */
export function ComponentDialog({
  open,
  onOpenChange,
  assetId,
  component,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  component?: AssetComponentDto;
}) {
  const utils = trpc.useUtils();
  const create = trpc.maintenance.createComponent.useMutation();
  const update = trpc.maintenance.updateComponent.useMutation();
  const { errors, setErrors, formError, setFormError, fail } = useDialogErrors(COMPONENT_KEYS, open);
  const [kind, setKind] = useState<AssetComponentKind>('hosting');
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [included, setIncluded] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setKind(component?.kind ?? 'hosting');
    setDescription(component?.description ?? '');
    setProvider(component?.provider ?? '');
    setExpiresOn(component?.expiresOn ?? '');
    setIncluded(component?.included ?? true);
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const fields = {
      kind,
      description: orNull(description),
      provider: orNull(provider),
      expiresOn: orNull(expiresOn),
      included,
    };
    try {
      if (component) {
        const input = { ...fields, id: component.id, version: component.version };
        const parsed = updateComponentInput.safeParse(input);
        if (!parsed.success) return setErrors(issuesToErrors(zodIssues(parsed.error), COMPONENT_KEYS));
        await update.mutateAsync(input);
      } else {
        const input = { ...fields, assetId };
        const parsed = createComponentInput.safeParse(input);
        if (!parsed.success) return setErrors(issuesToErrors(zodIssues(parsed.error), COMPONENT_KEYS));
        await create.mutateAsync(input);
      }
      toast.success(component ? 'Zapisano element' : 'Dodano element');
      await utils.maintenance.invalidate();
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  const pending = create.isPending || update.isPending;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="m"
      title={component ? 'Edytuj element' : 'Dodaj element'}
      className={FULLSCREEN_ON_PHONE}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="component-form" loading={pending}>
            {component ? 'Zapisz' : 'Dodaj element'}
          </Button>
        </>
      }
    >
      <form id="component-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label="Rodzaj" error={errors.kind}>
          <Select
            value={kind}
            onValueChange={(v) => setKind(v as AssetComponentKind)}
            options={ASSET_COMPONENT_KINDS.map((k) => ({ value: k, label: ASSET_COMPONENT_KIND_LABELS[k] }))}
          />
        </Field>
        <Field label="Opis" error={errors.description}>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="np. Zenbox, pakiet Firma 10k"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Dostawca" error={errors.provider}>
            <Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="np. Let’s Encrypt" />
          </Field>
          <Field label="Ważny do" error={errors.expiresOn}>
            <DateInput value={expiresOn} onValueChange={setExpiresOn} />
          </Field>
        </div>
        <Checkbox checked={included} onCheckedChange={setIncluded} label="W pakiecie (bez osobnej opłaty)" />
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}

// ── Koszt dostawcy ──────────────────────────────────────────────────────────
const RENEWAL_KEYS = ['provider', 'amount', 'cycle', 'renewsOn', 'componentId', 'notes'] as const;
const CYCLES: readonly BillingCycle[] = ['year', 'quarter', 'month', 'other', 'none'];
const NONE = '__none__';

/**
 * Koszt dostawcy (domena, hosting): dostawca, kwota, cykl, data odnowienia, element. Przypomina tylko zespołowi
 * (30 i 7 dni przed). W edycji: „Odnowione” przesuwa datę o cykl, „Archiwizuj” kończy przypomnienia.
 */
export function SupplierRenewalDialog({
  open,
  onOpenChange,
  assetId,
  components,
  renewal,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  components: AssetComponentDto[];
  renewal?: SupplierRenewalDto;
}) {
  const utils = trpc.useUtils();
  const create = trpc.maintenance.createSupplierRenewal.useMutation();
  const update = trpc.maintenance.updateSupplierRenewal.useMutation();
  const renewed = trpc.maintenance.markSupplierRenewed.useMutation();
  const archive = trpc.maintenance.archiveSupplierRenewal.useMutation();
  const { errors, setErrors, formError, setFormError, fail } = useDialogErrors(RENEWAL_KEYS, open);
  const [provider, setProvider] = useState('');
  const [amount, setAmount] = useState('');
  const [cycle, setCycle] = useState<BillingCycle>('year');
  const [renewsOn, setRenewsOn] = useState('');
  const [componentId, setComponentId] = useState(NONE);
  const [notes, setNotes] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setProvider(renewal?.provider ?? '');
    setAmount(amountInputValue(renewal?.amount));
    setCycle(renewal?.cycle ?? 'year');
    setRenewsOn(renewal?.renewsOn ?? '');
    setComponentId(renewal?.componentId ?? NONE);
    setNotes(renewal?.notes ?? '');
  }, [open]);

  const after = async (message: string) => {
    toast.success(message);
    await utils.maintenance.invalidate();
    onOpenChange(false);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const parsedAmount = parseAmountInput(amount);
    if (!parsedAmount) return setErrors({ amount: 'Podaj kwotę, np. 99 albo 99,90' });
    const fields = {
      provider,
      amount: parsedAmount,
      cycle,
      renewsOn,
      componentId: componentId === NONE ? null : componentId,
      notes: orNull(notes),
    };
    try {
      if (renewal) {
        const input = { ...fields, id: renewal.id, version: renewal.version };
        const parsed = updateSupplierRenewalInput.safeParse(input);
        if (!parsed.success) return setErrors(issuesToErrors(zodIssues(parsed.error), RENEWAL_KEYS));
        await update.mutateAsync(input);
        await after('Zapisano koszt dostawcy');
      } else {
        const input = { ...fields, assetId };
        const parsed = createSupplierRenewalInput.safeParse(input);
        if (!parsed.success) return setErrors(issuesToErrors(zodIssues(parsed.error), RENEWAL_KEYS));
        await create.mutateAsync(input);
        await after('Dodano koszt dostawcy');
      }
    } catch (err) {
      fail(err);
    }
  };

  const markRenewed = async () => {
    if (!renewal) return;
    try {
      const next = await renewed.mutateAsync({ id: renewal.id, version: renewal.version });
      await after(`Odnowione — kolejne odnowienie ${dateText(next.renewsOn)}`);
    } catch (err) {
      fail(err);
    }
  };

  const archiveIt = async () => {
    if (!renewal) return;
    try {
      await archive.mutateAsync({ id: renewal.id, version: renewal.version });
      await after('Koszt dostawcy w archiwum');
    } catch (err) {
      fail(err);
    }
  };

  const pending = create.isPending || update.isPending || renewed.isPending || archive.isPending;
  const nextDate = renewal ? nextCycleDate(renewal.renewsOn, renewal.cycle, renewal.anchorDay) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="m"
      title={renewal ? 'Koszt dostawcy' : 'Dodaj koszt dostawcy'}
      className={FULLSCREEN_ON_PHONE}
      description="Przypomnienie trafia tylko do zespołu: 30 i 7 dni przed odnowieniem. Klient go nie dostaje."
      footer={
        <>
          {renewal && (
            <Button variant="ghost" icon={Archive} onClick={archiveIt} disabled={pending} className="mr-auto">
              Archiwizuj
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="renewal-form" loading={create.isPending || update.isPending}>
            {renewal ? 'Zapisz' : 'Dodaj koszt'}
          </Button>
        </>
      }
    >
      <form id="renewal-form" ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Dostawca" error={errors.provider}>
            <Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="np. Zenbox" />
          </Field>
          <Field label="Element" error={errors.componentId}>
            <Select
              value={componentId}
              onValueChange={setComponentId}
              options={[
                { value: NONE, label: 'Cały zasób' },
                ...components.map((c) => ({
                  value: c.id,
                  label: c.description ?? ASSET_COMPONENT_KIND_LABELS[c.kind],
                })),
              ]}
            />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Kwota netto" error={errors.amount}>
            <Input
              mono
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="99"
            />
          </Field>
          <Field label="Cykl" error={errors.cycle}>
            <Select
              value={cycle}
              onValueChange={(v) => setCycle(v as BillingCycle)}
              options={CYCLES.map((c) => ({ value: c, label: BILLING_CYCLE_LABELS[c] }))}
            />
          </Field>
          <Field label="Odnowienie" error={errors.renewsOn}>
            <DateInput value={renewsOn} onValueChange={setRenewsOn} />
          </Field>
        </div>
        <Field label="Notatka" error={errors.notes}>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="np. Domena helpmycar.pl" />
        </Field>
        {renewal && nextDate && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-surface px-3 py-2.5">
            <p className="min-w-0 flex-1 text-caption text-fg-secondary">
              Opłacone u dostawcy? Kolejne odnowienie przesunie się na {dateText(nextDate)}.
            </p>
            <Button variant="secondary" size="s" icon={RefreshCw} onClick={markRenewed} loading={renewed.isPending}>
              Odnowione
            </Button>
          </div>
        )}
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
